import axios from 'axios';
import {
  ArrowUpRight,
  Loader2,
  MapPin,
  MessageSquarePlus,
  MessagesSquare,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { addConversationToCustomerConversations } from '@/actions/webchat';
import { HideMobileTabBar } from '@/components/mobile';
import { CustomerDialog } from '@/components/runner/customer/CustomerDialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useLazyGetCustomerByIdQuery } from '@/slices/customerSlice';
import { buildCanonicalCustomerConversationPath } from '@/utils/customerConversationRoutes';
import formatPhoneForTitleBar from '@/utils/formatPhoneForUser';
import getWebchatUrl from '@/utils/getWebchatUrl';
import ChatBox from '../ChatBox';
import ChatFeed from '../Chatfeed/ChatFeed';
import RecipientField from './RecipientField';

// Shared with the legacy new-message screen so a user's preferred location
// carries over between the two compose entry points.
const DEFAULT_LOCATION_STORAGE_KEY = 'newMessageDefaultLocationId';

// A phone held sideways (the desktop layout, about 340–430px tall): as in
// Messages, the To header keeps its height, ChatBox takes what it needs and
// the preview or empty state gets what is left, so the field and Send stay on
// screen. Rows: header, preview, composer. With the keyboard up (110–200px
// left) the header also drops its title row and padding.
const SIDEWAYS_ROWS =
  'max-xl:[@media(max-height:560px)]:grid max-xl:[@media(max-height:560px)]:grid-cols-[minmax(0,1fr)] max-xl:[@media(max-height:560px)]:grid-rows-[auto_minmax(0,1fr)_minmax(0,auto)]';
const SIDEWAYS_CLIP = 'max-xl:[@media(max-height:560px)]:overflow-hidden';
const SIDEWAYS_TYPING_HIDE = 'kb-open:max-xl:[@media(max-height:560px)]:hidden';

// Route a "create from search" term into the right CustomerDialog field:
// contains @ -> email, all digits -> phone, otherwise -> name.
const buildCreateInitialFromTerm = (term) => {
  const trimmed = (term || '').trim();
  const safe = trimmed.replace(/[\W_]+/g, '');
  if (trimmed.includes('@')) return { name: '', phone: '', email: trimmed };
  if (/^\d+$/.test(safe)) return { name: '', phone: trimmed, email: '' };
  return { name: trimmed, phone: '', email: '' };
};

/**
 * iOS-style "new conversation" view (GROUP_SMS_PLAN.md Phase 4 UX):
 * selecting a recipient never navigates — the matched conversation for the
 * CURRENT recipient set renders as a read-only inline thread preview, and the
 * composer always sends to the current set (1 → 1:1 endpoints, 2+ → group).
 * After a successful send we navigate to the conversation ("graduation").
 */
// Phones (isMobile, from WebchatDashboard): a native "New Message" screen with
// Cancel on the left. Without the props it stays the desktop view.
export default function ComposeView({ sendMessageToNewCustomer, isMobile = false, onMobileBack }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const socketId = useSelector((state) => state.webchat?.socketId);
  const auth = useSelector((state) => state.auth);
  const businessGroup = useSelector((state) => state.businessGroup) || [];
  const isEnterpriseEnabled = !!auth?.user?.isEnterpriseEnabled;

  // "Add people" from a 1:1 conversation header pre-fills that customer
  const prefillRecipient = location.state?.prefillRecipient;
  const [recipients, setRecipients] = useState(prefillRecipient ? [prefillRecipient] : []);
  const [openPaymentRequest] = useState(Boolean(location.state?.openPaymentRequest));
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [groupStatus, setGroupStatus] = useState({ enabled: false, maxCustomers: 8 });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createInitial, setCreateInitial] = useState({ name: '', phone: '', email: '' });
  // Which recipient chip (if any) the dialog is currently editing.
  const [editingIndex, setEditingIndex] = useState(null);
  const [triggerGetCustomerById] = useLazyGetCustomerByIdQuery();
  // Enterprise only: which location a brand-new customer is created under.
  const [selectedLocation, setSelectedLocation] = useState('');
  const previewFeedRef = useRef(null);

  // Arriving from the search bar's "Click to create" — open the create dialog
  // pre-filled with the searched term. Clear the one-shot router state after
  // consuming it so a re-render/back-nav doesn't reopen the dialog.
  useEffect(() => {
    const term = location.state?.createCustomerPrefill;
    if (!term) return;
    setEditingIndex(null);
    setCreateInitial(buildCreateInitialFromTerm(term));
    setCreateDialogOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  // Seed the location picker with the saved default (or the first location).
  useEffect(() => {
    if (!isEnterpriseEnabled || selectedLocation || !businessGroup.length) return;
    const saved = localStorage.getItem(DEFAULT_LOCATION_STORAGE_KEY);
    const isSavedValid = saved && businessGroup.some((b) => String(b._id) === String(saved));
    setSelectedLocation(isSavedValid ? String(saved) : String(businessGroup[0]._id));
  }, [isEnterpriseEnabled, businessGroup, selectedLocation]);

  const webchatApiUrl = getWebchatUrl(window.location.href);
  // group-sms/status is on for every eligible business (+1 non-Bandwidth
  // number); ineligible or admin-disabled businesses stay capped at 1 recipient.
  const maxRecipients = groupStatus.enabled ? groupStatus.maxCustomers : 1;
  const isGroupCompose = recipients.length >= 2;
  const firstRecipient = recipients[0] || null;

  // When composing to a single existing customer, expose its _customer/_business
  // to ChatBox so customer-scoped actions (invoices, payment requests) work.
  // We intentionally omit the conversation _id so message sends still route
  // through the new-customer endpoint (handleSend), not the no-op sendMessage.
  //
  // _business carries the customer's location so enterprise invoices/payments
  // resolve to the correct business: getBusinessFromRequest (used by both
  // requireStripeAccount and create-invoice) honors req.body._business for
  // enterprise users. Falls back to the compose location param, then undefined
  // (non-enterprise → server uses the authed business).
  const composerLocation =
    firstRecipient?._business || selectedLocation || searchParams.get('_location') || undefined;
  const composerActiveConversation =
    recipients.length === 1 && firstRecipient?._customer
      ? {
          _business: composerLocation,
          _customer: {
            _id: firstRecipient._customer,
            name: firstRecipient.name,
            phone: firstRecipient.phoneEmail?.includes('@') ? undefined : firstRecipient.phoneEmail,
            email: firstRecipient.phoneEmail?.includes('@') ? firstRecipient.phoneEmail : undefined,
          },
        }
      : {};

  // can this business start group chats? (server-driven eligibility)
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${webchatApiUrl}/api/webchat/group-sms/status`, { withCredentials: true })
      .then((res) => {
        if (!cancelled && !res.data?.error) {
          setGroupStatus({
            enabled: !!res.data.enabled,
            maxCustomers: res.data.maxCustomers || 8,
          });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [webchatApiUrl]);

  // resolve the thread preview for the current recipient set. Only a set of
  // all-existing customers can match a conversation; any new contact means
  // there's nothing to preview yet.
  useEffect(() => {
    const existingIds = recipients.filter((r) => r._customer).map((r) => r._customer);

    if (recipients.length === 0 || existingIds.length !== recipients.length) {
      setPreview(null);
      return undefined;
    }

    let cancelled = false;
    setPreviewLoading(true);

    axios
      .post(
        `${webchatApiUrl}/api/webchat/find-conversation-for-recipients`,
        { _customers: existingIds },
        { withCredentials: true },
      )
      .then((res) => {
        if (!cancelled) setPreview(res.data?.customerConversation || null);
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [recipients, webchatApiUrl]);

  const graduate = (conversation) => {
    navigate(
      buildCanonicalCustomerConversationPath({
        _customerConversation: conversation._id,
        _business: conversation._business,
      }),
      // Phones: the conversation replaces this screen, so Back goes to the list.
      isMobile ? { replace: true } : undefined,
    );
  };

  const cancelCompose = () => {
    if (isMobile && onMobileBack) {
      onMobileBack();
      return;
    }
    navigate('/inbox');
  };

  // Save in CustomerDialog creates the customer + conversation up-front (via
  // /api/create-customer) so a real _customer id exists for downstream features
  // (invoices, payment requests, etc.). 1:1 graduates straight into the new
  // conversation; group-enabled compose adds the customer as a real existing
  // chip and keeps composing. Throwing here lets CustomerDialog surface the
  // error and keep itself open.
  const handleCreateContact = async (data) => {
    const phone = (data.phone || '').trim();
    const email = (data.email || '').trim().toLowerCase();
    const phoneEmail = phone || email;
    if (!phoneEmail) throw new Error('Enter a phone number or email.');

    const _location = selectedLocation || searchParams.get('_location') || '';
    const res = await axios.post(
      '/api/create-customer',
      {
        customer: {
          name: data.name,
          phoneEmail,
          phone,
          email,
          details: data.details,
          tags: data.tags,
          billingEmail: data.billingEmail,
          billingAddress: data.billingAddress,
          associatedLinks: data.associatedLinks,
          _location,
        },
      },
      { withCredentials: true },
    );

    if (res.data?.error) throw new Error(res.data.message || 'Could not create customer.');

    const { customer, customerConversation } = res.data;

    // 1:1 (no groups) — jump straight into the real conversation
    if (!groupStatus.enabled) {
      dispatch(addConversationToCustomerConversations(customerConversation, true));
      graduate(customerConversation);
      return;
    }

    // group-enabled — add the now-real customer as an existing chip, keep composing
    setRecipients((prev) => [
      ...prev,
      {
        key: customer._id,
        _customer: customer._id,
        _business: customer._business,
        name: customer.name || phoneEmail,
        phoneEmail: customer.phone || customer.email || phoneEmail,
        isNew: false,
      },
    ]);
    setCreateDialogOpen(false);
  };

  // Open the contact editor for an already-added recipient. New (unsent) chips
  // edit locally; saved customers load their full record first so a save can't
  // blank fields the dialog doesn't surface as recipient data (tags, billing).
  const handleEditRecipient = async (index) => {
    const r = recipients[index];
    if (!r) return;

    const isE = (r.phoneEmail || '').includes('@');
    const phone = isE ? '' : r.phoneEmail || '';
    const email = isE ? r.phoneEmail || '' : '';

    // A freshly auto-committed chip is named after its number/email — start the
    // name blank so the user can type a real one without deleting the value.
    const isAutoName =
      r.isNew &&
      (r.name === r.phoneEmail || (!isE && r.name === formatPhoneForTitleBar(r.phoneEmail)));
    const name = isAutoName ? '' : r.name || '';

    if (r._customer) {
      try {
        const res = await triggerGetCustomerById(r._customer).unwrap();
        const full = res?.entities?.[r._customer] || {};
        setCreateInitial({
          _id: r._customer,
          _business: full._business || r._business,
          name: full.name ?? r.name ?? '',
          phone: full.phone ?? (isE ? '' : r.phoneEmail) ?? '',
          email: full.email ?? (isE ? r.phoneEmail : '') ?? '',
          details: full.details ?? '',
          tags: full.tags ?? [],
          billingEmail: full.billingEmail ?? '',
          billingAddress: full.billingAddress ?? '',
          associatedLinks: full.associatedLinks ?? [],
        });
      } catch {
        toast.error('Could not load this contact to edit. Please try again.');
        return;
      }
    } else {
      setCreateInitial({ name, phone, email });
    }

    setEditingIndex(index);
    setCreateDialogOpen(true);
  };

  // Sync a chip from the dialog's saved data — used for both the local new-chip
  // save and the saved-customer onSaved callback.
  const applyEditToChip = (data) => {
    const phoneEmail = (data.phone || data.email || '').trim();
    setRecipients((prev) =>
      prev.map((rr, i) =>
        i === editingIndex ? { ...rr, name: data.name || phoneEmail, phoneEmail } : rr,
      ),
    );
    setCreateDialogOpen(false);
    setEditingIndex(null);
  };

  const handleSend = async (messageData) => {
    // 1:1 — existing find-or-create endpoint handles both new and known customers
    if (!isGroupCompose) {
      const res = await sendMessageToNewCustomer(messageData);
      if (!res?.error && res?.customerConversation?._id) {
        graduate(res.customerConversation);
      }
      return res;
    }

    // group send
    if (messageData.scheduledSendDate) {
      toast.error('Scheduling isn’t available for group messages yet.');
      return { error: true };
    }

    try {
      const res = await axios.post(
        `${webchatApiUrl}/api/webchat/business-message-to-group`,
        {
          _customers: recipients.filter((r) => r._customer).map((r) => r._customer),
          newContacts: recipients
            .filter((r) => r.isNew)
            .map((r) => ({ name: r.name, phone: r.phoneEmail })),
          text: messageData.text,
          attachmentUrls: messageData.attachmentUrls || [],
          fromWebsocketId: socketId,
        },
        { withCredentials: true },
      );

      if (res.data?.error) {
        toast.error(res.data.message || 'Failed to send group message.');
        return { error: true };
      }

      const conversation = res.data.customerConversation;
      dispatch(addConversationToCustomerConversations(conversation, true));
      graduate(conversation);
      return { error: false };
    } catch (err) {
      console.error('group send failed', err);
      toast.error('Failed to send group message.');
      return { error: true };
    }
  };

  const renderPreviewArea = () => {
    if (previewLoading && !preview) {
      return (
        <div className={cn('flex-1 flex flex-col items-center justify-center gap-2 text-muted-foreground', !isMobile && SIDEWAYS_CLIP)}>
          <Loader2 className="size-5 animate-spin" />
        </div>
      );
    }

    if (preview?._id) {
      return (
        <div className={cn('flex-1 flex flex-col min-h-0', !isMobile && SIDEWAYS_CLIP)}>
          <div className="flex items-center justify-between gap-3 border-b bg-muted/50 px-4 py-2.5 max-md:py-2">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <MessagesSquare className="size-4" />
              </span>
              <div className="flex min-w-0 flex-col">
                <span className="text-sm font-semibold text-foreground">
                  {preview.isGroup ? 'Existing group conversation' : 'Existing conversation'}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {preview.isGroup
                    ? 'This group already has a thread — open it to continue.'
                    : `You already have a conversation with ${firstRecipient?.name || 'this contact'} — open it to continue.`}
                </span>
              </div>
            </div>
            <Button
              type="button"
              size="sm"
              className="h-8 shrink-0 gap-1.5 max-md:h-11"
              onClick={() => graduate(preview)}
            >
              {isMobile ? 'Open' : 'Open conversation'} <ArrowUpRight className="size-3.5" />
            </Button>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            <ChatFeed
              messages={preview.messages || []}
              activeConversation={preview}
              loadingNewMessage={false}
              innerRef={previewFeedRef}
              isMobile={isMobile}
            />
          </div>
        </div>
      );
    }

    return (
      <div className={cn('flex-1 flex flex-col items-center justify-center gap-2 text-muted-foreground px-6 text-center', !isMobile && SIDEWAYS_CLIP)}>
        {recipients.length === 0 ? (
          <>
            <MessageSquarePlus className={cn('size-8 opacity-50', !isMobile && SIDEWAYS_TYPING_HIDE)} />
            <p className="text-sm">Add a recipient to start a conversation</p>
            {groupStatus.enabled && (
              <p className="text-xs opacity-70">
                Add up to {groupStatus.maxCustomers} people for a group chat
              </p>
            )}
          </>
        ) : isGroupCompose ? (
          <>
            <Users className={cn('size-8 opacity-50', !isMobile && SIDEWAYS_TYPING_HIDE)} />
            <p className="text-sm">
              New group with{' '}
              <span className="font-medium text-foreground">
                {recipients.map((r) => r.name || r.phoneEmail).join(', ')}
              </span>
            </p>
            <p className="text-xs opacity-70">
              Everyone will see each other’s messages in one thread
            </p>
          </>
        ) : (
          <>
            <MessageSquarePlus className={cn('size-8 opacity-50', !isMobile && SIDEWAYS_TYPING_HIDE)} />
            <p className="text-sm">
              No previous conversation with{' '}
              <span className="font-medium text-foreground">
                {firstRecipient.name || firstRecipient.phoneEmail}
              </span>
              . Say hello!
            </p>
          </>
        )}
      </div>
    );
  };

  return (
    <div className={cn('flex flex-col h-full w-full bg-background', !isMobile && SIDEWAYS_ROWS)}>
      {/* single header block: compact title + cancel, with the To: field
          directly beneath inside the same bordered container */}
      <div className="border-b" data-onboarding="inbox-compose">
        {isMobile ? (
          <>
            <HideMobileTabBar />
            <div className="grid h-12 grid-cols-[1fr_auto_1fr] items-center px-1.5">
              <button
                type="button"
                onClick={cancelCompose}
                className="inline-flex h-11 min-w-11 items-center justify-self-start rounded-lg px-2.5 text-[17px] text-primary active:opacity-50"
              >
                Cancel
              </button>
              <h2 className="text-[17px] font-semibold">New Message</h2>
              <span />
            </div>
          </>
        ) : (
          <div className={cn('flex items-center justify-between px-4 pt-2 pb-0', SIDEWAYS_TYPING_HIDE)}>
            <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              New conversation
            </h2>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7 -mr-1.5 text-muted-foreground"
              aria-label="Cancel new conversation"
              onClick={cancelCompose}
            >
              <X className="size-4" />
            </Button>
          </div>
        )}

        <RecipientField
          recipients={recipients}
          onAdd={(r) => setRecipients((prev) => [...prev, r])}
          onRemove={(index) => setRecipients((prev) => prev.filter((_, i) => i !== index))}
          maxRecipients={maxRecipients}
          className={
            isMobile
              ? 'px-4 pb-2 pt-0'
              : 'px-4 pb-3 pt-0.5 kb-open:max-xl:[@media(max-height:560px)]:py-1'
          }
          isMobile={isMobile}
          onRequestCreateContact={(initial) => {
            setEditingIndex(null);
            setCreateInitial(initial);
            setCreateDialogOpen(true);
          }}
          onEdit={handleEditRecipient}
        />

        {/* Enterprise: pick which location a brand-new customer is created
            under. Only relevant before a recipient is chosen — an existing
            recipient already pins the location. */}
        {isEnterpriseEnabled && businessGroup.length > 1 && recipients.length === 0 && (
          <div className={cn('flex items-center gap-2 border-t px-4 py-2', !isMobile && SIDEWAYS_TYPING_HIDE)}>
            <MapPin className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Location
            </span>
            <Select
              value={selectedLocation}
              onValueChange={(val) => {
                setSelectedLocation(val);
                try {
                  localStorage.setItem(DEFAULT_LOCATION_STORAGE_KEY, val);
                } catch {
                  /* best-effort persistence */
                }
              }}
            >
              <SelectTrigger className="h-8 max-w-xs flex-1 max-md:h-11 max-md:max-w-none max-md:text-base">
                <SelectValue placeholder="Choose location" />
              </SelectTrigger>
              <SelectContent>
                {businessGroup.map((b) => (
                  <SelectItem key={b._id} value={String(b._id)}>
                    {b.name || b.address}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {renderPreviewArea()}

      <ChatBox
        activeConversation={composerActiveConversation}
        isGroupMode={isGroupCompose}
        autoFocusOnMount={false}
        openPaymentsOnMount={openPaymentRequest}
        sendMessage={async () => ({ error: true })}
        sendMessageToNewCustomer={handleSend}
        name={firstRecipient?.name || ''}
        phoneEmail={firstRecipient?.phoneEmail || ''}
        _location={composerLocation || ''}
        employee={localStorage.getItem('employeeName') || ''}
        resetNameAndPhoneEmail={() => {}}
        generateAIResponse={() => {}}
        isMobile={isMobile}
      />

      <CustomerDialog
        open={createDialogOpen}
        setOpen={(v) => {
          setCreateDialogOpen(v);
          if (!v) setEditingIndex(null);
        }}
        customerInfo={createInitial}
        createCustomerAndLinkToProject={
          editingIndex != null ? applyEditToChip : handleCreateContact
        }
        onSaved={editingIndex != null ? applyEditToChip : undefined}
        title={editingIndex != null ? 'Edit recipient' : undefined}
        _project={undefined}
      />
    </div>
  );
}
